/* ==========================================================================
   IRON FIT — the bridge from our cart to Shopify's

   WHY THIS EXISTS

   The Iron Fit cart is localStorage. That is deliberate: it holds things
   Shopify has no field for — which add-ons were ticked, what a package
   contains, the picture to show — and it lets someone build an order
   across pages without a round trip for every tap.

   EasySell cannot see any of that. It is a Shopify app: it reads the
   SHOPIFY cart, and its COD form creates the order from it. So the two
   have to be reconciled before the customer is handed over, and this is
   the thing that reconciles them.

   WHAT IT DOES
     · turns our cart into Shopify line items — the same flattening the
       order worker did, because the shapes have not changed: add-ons
       become their own lines, and a weight of plates becomes the pieces
       that make it up
     · resolves each key to a live variant id by asking Shopify, rather
       than carrying a table of ids that goes stale the first time a
       product is recreated in the admin
     · replaces the Shopify cart with ours and reports anything it could
       not place

   WHAT IT DOES NOT DO
     It never invents a line. If a key has no Shopify product — which is
     the case for the newer package tiers until they are created — it is
     returned in `missing` and the caller has to say so out loud. Silently
     dropping a $1,049 package from an order is the one failure mode that
     must not be possible here.
   ========================================================================== */
(function () {
  'use strict';

  /* key -> Shopify handle. Multi-variant products carry the number we have
     to match inside that product; everything else is a single variant.

     Handles, not ids: a handle survives a product being edited, and an id
     does not survive it being recreated. */
  var MAP = {
    /* machines */
    'compact':            { handle: 'single-stack-dual-function-machine' },
    'crossover':          { handle: 'dual-cable-crossover' },
    'smith':              { handle: 'iron-fit-smith-machine-multifunctional-dual-cable' },

    /* accessories */
    'bench':              { handle: 'commercial-adjustable-bench' },
    'bar':                { handle: 'olympic-barbell-7-ft' },
    'dbset':              { handle: '50-kg-chrome-dumbbell-set' },

    /* loose plates — one product, a variant per piece size */
    'plate-2.5':          { handle: 'rubber-bumper-plates-per-kg', kg: 2.5 },
    'plate-5':            { handle: 'rubber-bumper-plates-per-kg', kg: 5 },
    'plate-10':           { handle: 'rubber-bumper-plates-per-kg', kg: 10 },
    'plate-20':           { handle: 'rubber-bumper-plates-per-kg', kg: 20 },

    /* plate upgrades that ride along with a package */
    'upg-plates-10':      { handle: 'iron-fit-extra-plates-package-upgrade', kg: 10 },
    'upg-plates-20':      { handle: 'iron-fit-extra-plates-package-upgrade', kg: 20 },
    'upg-plates-30':      { handle: 'iron-fit-extra-plates-package-upgrade', kg: 30 },
    'upg-plates-40':      { handle: 'iron-fit-extra-plates-package-upgrade', kg: 40 },
    'upg-plates-50':      { handle: 'iron-fit-extra-plates-package-upgrade', kg: 50 },
    'upg-plates-60':      { handle: 'iron-fit-extra-plates-package-upgrade', kg: 60 },
    'upg-plates-70':      { handle: 'iron-fit-extra-plates-package-upgrade', kg: 70 },
    'upg-plates-80':      { handle: 'iron-fit-extra-plates-package-upgrade', kg: 80 },

    /* tool sets */
    'tool-socket46':      { handle: '46-piece-socket-ratchet-set' },
    'tool-precision115':  { handle: '115-in-1-precision-screwdriver-set' },
    'tool-pro108':        { handle: '108-in-1-professional-tool-set' },
    'tool-repair128':     { handle: '128-in-1-complete-repair-kit' },

    /* packages. The One-Machine Gym has no product of its own — it is the
       compact machine at the same $570, which is why it points there. */
    'pkg-compact':        { handle: 'single-stack-dual-function-machine' },
    'pkg-famous':         { handle: '899-home-gym-bundle' },
    'pkg-famous-40':      { handle: 'famous-gym-40kg' },
    'pkg-famous-60':      { handle: 'famous-gym-60kg' },
    'pkg-famous-80':      { handle: 'famous-gym-80kg' },
    'pkg-smith-starter':  { handle: 'iron-fit-smith-starter-smith-machine-bench-40-kg-plates' },
    'pkg-smith-complete': { handle: 'iron-fit-smith-complete-smith-machine-bench-7-ft-bar-60-kg-plates' },
    'pkg-smith-heavy':    { handle: 'iron-fit-smith-heavy-smith-machine-bench-7-ft-bar-80-kg-plates' }
  };

  var LABEL = {
    'pkg-famous-40': 'The Famous Gym — 40 KG',
    'pkg-famous-60': 'The Famous Gym — 60 KG',
    'pkg-famous-80': 'The Famous Gym — 80 KG'
  };

  /* ----------------------------------------------------------------------
     OUR CART -> A FLAT LIST OF KEYS

     Straight port of what ifz-cod.js sent the order worker. A machine with
     a bench ticked is two lines, because it is two things being delivered.
     ---------------------------------------------------------------------- */
  function splitPlates(kg, sizes, prefix) {
    var out = [], left = Math.round((kg || 0) * 10) / 10;
    sizes.forEach(function (s) {
      var n = Math.floor(left / s);
      if (n > 0) { out.push({ key: prefix + s, qty: n }); left = Math.round((left - n * s) * 10) / 10; }
    });
    return out;
  }

  function linesFor(item) {
    var out = [];
    function push(key, kg, qty) {
      if (!key) return;
      if (key === 'plates')     { out = out.concat(splitPlates(kg || 0, [20, 10, 5, 2.5], 'plate-')); return; }
      if (key === 'upg-plates') { out = out.concat(splitPlates(kg || 0, [80, 70, 60, 50, 40, 30, 20, 10], 'upg-plates-')); return; }
      out.push({ key: key, qty: qty || 1 });
    }
    push(item.key, item.kg, window.IFZCart ? window.IFZCart.qtyOf(item) : 1);
    (item.extras || []).forEach(function (e) { push(e.key, e.kg, 1); });
    return out;
  }

  function lines(items) {
    var all = [];
    (items || []).forEach(function (it) { all = all.concat(linesFor(it)); });
    var merged = {};
    all.forEach(function (l) { merged[l.key] = (merged[l.key] || 0) + l.qty; });
    return Object.keys(merged).map(function (k) { return { key: k, qty: merged[k] }; });
  }

  /* ----------------------------------------------------------------------
     KEY -> LIVE VARIANT ID

     Asked of Shopify rather than hard-coded. One fetch per product, held
     for the life of the page; a cart is three or four products at most.
     ---------------------------------------------------------------------- */
  var pending = {};

  function product(handle) {
    if (pending[handle]) return pending[handle];
    pending[handle] = fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    return pending[handle];
  }

  /* The first number in a variant title is the size it refers to:
     "2.5 kg", "20 kg", "+40 KG". Matching on that rather than the whole
     string means a rename to "+40 KG plates" does not break ordering. */
  function pickVariant(p, kg) {
    if (!p || !p.variants || !p.variants.length) return null;
    if (kg == null) return p.variants[0].id;
    for (var i = 0; i < p.variants.length; i++) {
      var m = /(\d+(?:\.\d+)?)/.exec(p.variants[i].title || '');
      if (m && parseFloat(m[1]) === kg) return p.variants[i].id;
    }
    return null;
  }

  function variantFor(key) {
    var e = MAP[key];
    if (!e) return Promise.resolve(null);
    return product(e.handle).then(function (p) { return pickVariant(p, e.kg); });
  }

  /* ----------------------------------------------------------------------
     REPLACE SHOPIFY'S CART WITH OURS

     Cleared first, deliberately. Adding without clearing would double an
     order every time somebody went back to the cart page, and a doubled
     cash-on-delivery order is a driver turning up with two machines.
     ---------------------------------------------------------------------- */
  function sync(items) {
    var want = lines(items);
    if (!want.length) {
      return post('/cart/clear.js', {}).then(function () {
        return { ok: true, count: 0, missing: [] };
      });
    }

    return Promise.all(want.map(function (l) {
      return variantFor(l.key).then(function (id) {
        return { key: l.key, qty: l.qty, id: id };
      });
    })).then(function (resolved) {
      var good = resolved.filter(function (r) { return r.id; });
      var missing = resolved.filter(function (r) { return !r.id; })
                            .map(function (r) { return LABEL[r.key] || r.key; });

      /* Anything we cannot place means the Shopify cart is emptied and left
         empty — not filled with the part we could manage.

         Two ways a leftover cart bites otherwise. A part-filled one can be
         ordered from anywhere that is not this page, and the customer pays
         for a crossover while believing they bought the package it came in.
         And whatever was in there from a previous visit simply stays,
         which is worse: it has nothing to do with what is on screen now. */
      if (missing.length) {
        return post('/cart/clear.js', {}).then(function () {
          return { ok: false, count: 0, missing: missing };
        });
      }

      return post('/cart/clear.js', {})
        .then(function () {
          return post('/cart/add.js', {
            items: good.map(function (r) { return { id: r.id, quantity: r.qty }; })
          });
        })
        .then(function (res) {
          return { ok: !!res, count: good.length, missing: [] };
        })
        .catch(function () {
          /* half-added is the dangerous state, so leave nothing behind */
          return post('/cart/clear.js', {}).then(function () {
            return { ok: false, count: 0, missing: [], error: true };
          });
        });
    });
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.ok ? r.json() : null; });
  }

  window.IFZShopify = {
    map: MAP,
    lines: lines,
    variantFor: variantFor,
    sync: sync
  };
})();
